import { useMemo } from "react";
import { useGetList } from "ra-core";

import type { Deal, DealNote, PipelineStage } from "../types";

export const STALE_AFTER_DAYS = 7;
const DAY = 24 * 60 * 60 * 1000;

export type StaleDeal = { deal: Deal; idleDays: number };

/**
 * «Сделки без движения 7 дней»: open deals (not on a won or lost stage of
 * their pipeline, not archived) with
 * no change to the deal and no note for the last 7 days. The access rules of
 * the database decide whose deals these are (a manager sees only theirs).
 */
export const useStaleDeals = () => {
  const cutoff = useMemo(
    () => new Date(Date.now() - STALE_AFTER_DAYS * DAY).toISOString(),
    [],
  );

  const { data: candidates, isPending: isPendingDeals } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "updated_at", order: "ASC" },
      filter: { "archived_at@is": null, "updated_at@lt": cutoff },
    },
  );

  // closed = on a «won» or «lost» stage of its pipeline
  const { data: stages, isPending: isPendingStages } =
    useGetList<PipelineStage>("pipeline_stages", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "position", order: "ASC" },
    });
  const openDeals = useMemo(() => {
    const closed = new Set(
      (stages ?? [])
        .filter((stage) => stage.outcome !== "open")
        .map((stage) => String(stage.id)),
    );
    return (candidates ?? []).filter(
      (deal) => !closed.has(String(deal.stage_id)),
    );
  }, [candidates, stages]);
  const ids = openDeals.map((deal) => deal.id);

  // latest notes first, so the newest note of each deal is always in the page
  const { data: notes, isPending: isPendingNotes } = useGetList<DealNote>(
    "deal_notes",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "date", order: "DESC" },
      filter: { "deal_id@in": `(${ids.join(",")})` },
    },
    { enabled: ids.length > 0 },
  );

  const staleDeals = useMemo<StaleDeal[]>(() => {
    const lastNoteByDeal = new Map<string, string>();
    for (const note of notes ?? []) {
      const key = String(note.deal_id);
      if (!lastNoteByDeal.has(key) && note.date) {
        lastNoteByDeal.set(key, note.date);
      }
    }
    const now = Date.now();
    return openDeals
      .map((deal) => {
        const lastNote = lastNoteByDeal.get(String(deal.id));
        const lastMove = Math.max(
          Date.parse(deal.updated_at ?? deal.created_at),
          lastNote ? Date.parse(lastNote) : 0,
        );
        return { deal, lastMove, idleDays: Math.floor((now - lastMove) / DAY) };
      })
      .filter(({ lastMove }) => lastMove < Date.parse(cutoff))
      .sort((a, b) => a.lastMove - b.lastMove)
      .map(({ deal, idleDays }) => ({ deal, idleDays }));
  }, [openDeals, notes, cutoff]);

  return {
    staleDeals,
    isPending:
      isPendingDeals || isPendingStages || (ids.length > 0 && isPendingNotes),
  };
};
